import type { UIMessage } from "ai";
import { audienceProfileV1Schema, type AudienceProfileV1 } from "@runcheetah/shared";

export type AssistantAudienceProfileCreationPreviewReference = {
  id: string;
  name: string;
  description: string | null;
  summary: string | null;
  profile: AudienceProfileV1;
  activateByDefault: boolean;
};

export function getAssistantAudienceProfileCreationPreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantAudienceProfileCreationPreviewReference[] {
  const references: AssistantAudienceProfileCreationPreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: unknown;
    };

    if (
      candidate.type !== "tool-preview_audience_profile_creation" ||
      candidate.state !== "output-available"
    ) {
      continue;
    }

    const reference = parseAssistantAudienceProfileCreationPreviewReference(
      candidate.output,
      typeof candidate.toolCallId === "string" ? candidate.toolCallId : `audience-profile-${index}`,
    );
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}

export function isAssistantAudienceProfileCreationProposalStale(
  messages: readonly Pick<UIMessage, "id" | "role">[],
  proposalMessageId: string,
) {
  const proposalMessageIndex = messages.findIndex((message) => message.id === proposalMessageId);
  if (proposalMessageIndex === -1) {
    return false;
  }

  return messages.slice(proposalMessageIndex + 1).some((message) => message.role === "user");
}

function parseAssistantAudienceProfileCreationPreviewReference(
  value: unknown,
  id: string,
): AssistantAudienceProfileCreationPreviewReference | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const output = value as {
    render?: { component?: unknown };
    name?: unknown;
    description?: unknown;
    summary?: unknown;
    profile?: unknown;
    activateByDefault?: unknown;
  };

  if (output.render?.component !== "audience_profile_creation_confirmation") {
    return null;
  }

  const name = typeof output.name === "string" ? output.name.replace(/\s+/g, " ").trim() : "";
  if (!name) {
    return null;
  }

  const profile = audienceProfileV1Schema.safeParse(output.profile);
  if (!profile.success) {
    return null;
  }

  const description =
    typeof output.description === "string" && output.description.trim()
      ? output.description.trim()
      : null;
  const summary =
    typeof output.summary === "string" && output.summary.trim()
      ? output.summary.trim()
      : (profile.data.summary ?? null);

  return {
    id,
    name,
    description,
    summary,
    profile: profile.data,
    activateByDefault: output.activateByDefault !== false,
  };
}
